import { useArticleContentRefMap } from "@EditArticleContainer/shared/contexts/ArticleContentRefContext";
import { getParagraphEditedData } from "@EditArticleContainer/shared/hooks/useParagraph";
import {
  REF_CODE_DEAL_PARTNER_A_NAME,
  REF_CODE_DEAL_PARTNER_A_RESPONSIBILITY,
  REF_CODE_DEAL_PARTNER_B_NAME,
  REF_CODE_DEAL_PARTNER_B_RESPONSIBILITY,
  REF_CODE_DEAL_PARTNER_C_NAME,
  REF_CODE_DEAL_PARTNER_C_RESPONSIBILITY,
  REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_DETAIL,
  REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_VALUE,
  REF_CODE_FINANCIAL_TERMS_LASC_OTHER_DETAIL,
  REF_CODE_FINANCIAL_TERMS_LASC_OTHER_TERMS,
  REF_CODE_FINANCIAL_TERMS_LASC_OTHER_VALUE,
  REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_DETAIL,
  REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_RATE,
  REF_CODE_FINANCIAL_TERMS_LASC_TOTAL,
  REF_CODE_FINANCIAL_TERMS_LASC_UPFRONT,
  REF_CODE_KEY_RIGHTS_LASC_ASSET,
  REF_CODE_KEY_RIGHTS_LASC_EXCLUSIVITY,
  REF_CODE_KEY_RIGHTS_LASC_INTENDED_USE,
  REF_CODE_KEY_RIGHTS_LASC_RIGHTS_LIMITATION,
  REF_CODE_KEY_RIGHTS_LASC_RIGHTS_SCOPE,
  REF_CODE_KEY_RIGHTS_LASC_TERRITORY,
  REF_CODE_KEY_RIGHTS_LASC_TYPE,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createRefCode } from "@EditArticleContainer/shared/libs/refCode/utils/createRefCode";
import { DEALS_PARAGRAPH_CODE } from "@EditArticleContainer/shared/constants/editArticle.constants";
import { FinancialTermsLASCContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/financialTermsLASCContent.types";
import { KeyRightsLASCContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/keyRightsLASCContent.types";
import { PartnerAxisLASCContent } from "@EditArticleContainer/shared/types/paragraphContents/contents/partnerAxisLASCContent.types";
import {
  cloneContent,
  isEmptyContent,
  overwriteFromRef,
} from "@EditArticleContainer/shared/utils/mergeParagraphContentFromRef";

const SECTION_CODE = "TY002";

export const useCreateDealLASCContent = () => {
  const refMap = useArticleContentRefMap();

  const buildPartnerAxisLASC = async (): Promise<
    PartnerAxisLASCContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.partnerAxisLASC,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as PartnerAxisLASCContent);
    const axis = next.text["Partner Axis"];

    overwriteFromRef(
      refMap,
      axis["Partner A"]?.[0]?.["Partner A Name"],
      REF_CODE_DEAL_PARTNER_A_NAME,
    );
    overwriteFromRef(
      refMap,
      axis["Partner A Responsibility & Rights"],
      REF_CODE_DEAL_PARTNER_A_RESPONSIBILITY,
    );
    overwriteFromRef(
      refMap,
      axis["Partner B"]?.[0]?.["Partner B Name"],
      REF_CODE_DEAL_PARTNER_B_NAME,
    );
    overwriteFromRef(
      refMap,
      axis["Partner B Responsibility & Rights"],
      REF_CODE_DEAL_PARTNER_B_RESPONSIBILITY,
    );
    overwriteFromRef(
      refMap,
      axis["Partner C"]?.[0]?.["Partner C Name"],
      REF_CODE_DEAL_PARTNER_C_NAME,
    );
    overwriteFromRef(
      refMap,
      axis["Partner C Responsibility & Rights"],
      REF_CODE_DEAL_PARTNER_C_RESPONSIBILITY,
    );

    return next;
  };

  const buildKeyRightsLASC = async (): Promise<
    KeyRightsLASCContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.keyRightsLASC,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as KeyRightsLASCContent);

    next.text["Rights Scope Table"]?.forEach((item, index) => {
      overwriteFromRef(
        refMap,
        item.Asset,
        createRefCode(REF_CODE_KEY_RIGHTS_LASC_ASSET, index),
      );
      overwriteFromRef(
        refMap,
        item["Rights Scope Type"],
        createRefCode(REF_CODE_KEY_RIGHTS_LASC_TYPE, index),
      );
      overwriteFromRef(
        refMap,
        item["Rights Scope"],
        createRefCode(REF_CODE_KEY_RIGHTS_LASC_RIGHTS_SCOPE, index),
      );
      overwriteFromRef(
        refMap,
        item["Rights Limitation"],
        createRefCode(REF_CODE_KEY_RIGHTS_LASC_RIGHTS_LIMITATION, index),
      );
      overwriteFromRef(
        refMap,
        item["Intended Use"],
        createRefCode(REF_CODE_KEY_RIGHTS_LASC_INTENDED_USE, index),
      );
      overwriteFromRef(
        refMap,
        item.Exclusivity,
        createRefCode(REF_CODE_KEY_RIGHTS_LASC_EXCLUSIVITY, index),
      );
      overwriteFromRef(
        refMap,
        item.Territory,
        createRefCode(REF_CODE_KEY_RIGHTS_LASC_TERRITORY, index),
      );
    });

    return next;
  };

  const buildFinancialTermsLASC = async (): Promise<
    FinancialTermsLASCContent | undefined
  > => {
    const data = await getParagraphEditedData({
      sectionCode: SECTION_CODE,
      paragraphCode: DEALS_PARAGRAPH_CODE.financialTermsLASC,
    });
    if (isEmptyContent(data)) return undefined;

    const next = cloneContent(data as FinancialTermsLASCContent);

    next.text["Main Financial Terms Table"]?.forEach((item, tableIndex) => {
      overwriteFromRef(
        refMap,
        item["Upfront Payment(LA&SC)"],
        createRefCode(REF_CODE_FINANCIAL_TERMS_LASC_UPFRONT, tableIndex),
      );
      overwriteFromRef(
        refMap,
        item["Total Payment(LA&SC)"],
        createRefCode(REF_CODE_FINANCIAL_TERMS_LASC_TOTAL, tableIndex),
      );

      item["Milestone Payment And Detail"]?.forEach((milestone, i) => {
        overwriteFromRef(
          refMap,
          milestone["Milestone Payment(LA&SC)"],
          createRefCode(
            REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_VALUE,
            tableIndex,
            i,
          ),
        );
        overwriteFromRef(
          refMap,
          milestone["Milestone Payment Detail(LA&SC)"],
          createRefCode(
            REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_DETAIL,
            tableIndex,
            i,
          ),
        );
      });

      item["Other Payment And Detail"]?.forEach((other, i) => {
        overwriteFromRef(
          refMap,
          other["Other Payment(LA&SC)"],
          createRefCode(
            REF_CODE_FINANCIAL_TERMS_LASC_OTHER_VALUE,
            tableIndex,
            i,
          ),
        );
        overwriteFromRef(
          refMap,
          other["Other Payment Detail(LA&SC)"],
          createRefCode(
            REF_CODE_FINANCIAL_TERMS_LASC_OTHER_DETAIL,
            tableIndex,
            i,
          ),
        );
      });

      item["Royalty(LA&SC)"]?.forEach((royalty, i) => {
        overwriteFromRef(
          refMap,
          royalty["Royalty Rate"],
          createRefCode(
            REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_RATE,
            tableIndex,
            i,
          ),
        );
        overwriteFromRef(
          refMap,
          royalty["Royalty Detail"],
          createRefCode(
            REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_DETAIL,
            tableIndex,
            i,
          ),
        );
      });
    });

    overwriteFromRef(
      refMap,
      next.text["Other Financial Terms"],
      REF_CODE_FINANCIAL_TERMS_LASC_OTHER_TERMS,
    );

    return next;
  };

  return { buildPartnerAxisLASC, buildKeyRightsLASC, buildFinancialTermsLASC };
};
